import { useEffect, useState } from 'react';

function remaining(expiresAtMs: number) {
  return Math.max(0, expiresAtMs - Date.now());
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function Segment({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <div className="flex min-w-[2.1rem] flex-col items-center rounded-md bg-primary px-1.5 py-1 shadow-sm">
        <span className="font-mono text-base font-semibold leading-none tabular-nums text-primary-foreground">
          {value}
        </span>
      </div>
      <span className="mt-1 text-[9px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
    </div>
  );
}

/**
 * Live countdown to an access-expiry timestamp, rendered as compact segmented
 * boxes (days / hours / minutes — no seconds), under a small label that tells
 * the user what the countdown is for. Ticks once per second so the minute
 * flips on time. `expiresAtSeconds` is unix seconds (0 = never, renders nothing).
 */
export function CountdownTimer({ expiresAtSeconds }: { expiresAtSeconds: number }) {
  const expiresAtMs = expiresAtSeconds * 1000;
  const [left, setLeft] = useState(() => remaining(expiresAtMs));

  useEffect(() => {
    if (!expiresAtSeconds) return;
    setLeft(remaining(expiresAtMs));
    const id = window.setInterval(() => setLeft(remaining(expiresAtMs)), 1000);
    return () => window.clearInterval(id);
  }, [expiresAtMs, expiresAtSeconds]);

  if (!expiresAtSeconds) return null;

  if (left <= 0) {
    return (
      <span role="status" className="inline-flex items-center rounded-full bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive">
        Zugang abgelaufen
      </span>
    );
  }

  const totalSeconds = Math.floor(left / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  return (
    <div
      className="flex flex-col items-end gap-1"
      aria-label="Verbleibende Zugriffszeit"
      title="So lange hast du noch Zugriff auf deine Kurse"
    >
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Dein Zugang endet in
      </span>
      <div className="flex items-center gap-1.5">
        {days > 0 && <Segment value={String(days)} label="Tage" />}
        <Segment value={pad2(hours)} label="Std" />
        <Segment value={pad2(minutes)} label="Min" />
      </div>
    </div>
  );
}
